import { takeTabParam, withLinkParam } from "@/utils/link-param"
import { createJSONStorage, localStorageSource, sessionStorageSource } from "./browser"

const keyList = {
  decode(value: unknown) {
    if (!Array.isArray(value) || !value.every((item) => typeof item === "string" && item.length > 0)) return []
    return [...new Set<string>(value)]
  },
}

/**
 * One sidebar selection, a UI preference. Each tab keeps its own selection
 * through reloads (sessionStorage); a new tab or a relaunched app starts from
 * the last selection chosen in any tab (localStorage).
 */
function createSelectionStorage(key: string) {
  const device = createJSONStorage<string[]>(key, keyList, localStorageSource)
  const tab = createJSONStorage<string[] | undefined>(key, keyList, sessionStorageSource)
  return {
    /** Also fixes this tab's selection, so selections chosen later in other tabs do not replace it on reload. */
    read() {
      const own = tab.read(undefined)
      if (own) return own
      const inherited = device.read([])
      tab.write(inherited)
      return inherited
    },
    write(values: string[]) {
      // The tab keeps an empty selection explicitly; removing it would inherit another tab's selection on reload.
      tab.write(values)
      device.write(values, { removeWhen: (items) => items.length === 0 })
    },
    clear() {
      tab.remove()
      device.remove()
    },
    /** Sets this tab's selection unless it has its own, as after a reload; tabs opened later still start from the device's. */
    scopeTab(values: string[]) {
      if (tab.read(undefined) === undefined) tab.write(values)
    },
  }
}

/** UI preference: which projects the sidebar and mobile dashboard show. Not server-authoritative. */
export function createSidebarProjectFilterStorage() {
  return createSelectionStorage("mux.sidebar-project-filter.v1")
}

/** UI preference: which machines the sidebar and mobile dashboard show. Not server-authoritative. */
export function createSidebarMachineFilterStorage() {
  return createSelectionStorage("mux.sidebar-machine-filter.v1")
}

const projectLinkParam = "sidebar-project"

/** `href`, an app path, for a new tab whose sidebar lists only `project`. */
export function sidebarProjectLink(href: string, project: string) {
  return withLinkParam(href, projectLinkParam, project)
}

/**
 * Applies a `sidebarProjectLink` to this tab's filter, unless the tab still has
 * its own, as after a reload. The project stays in the tab's address, so a
 * browser that reopens the tab without its sessionStorage shows the project
 * again. Call before the router reads the location. The device's selection is
 * unchanged.
 */
export function consumeSidebarProjectLink() {
  const project = takeTabParam(projectLinkParam)
  if (!project) return
  createSidebarProjectFilterStorage().scopeTab([project])
  createSidebarMachineFilterStorage().scopeTab([])
}

/** UI preference: whether the desktop sidebar hides its machine and project chips, on this device. Not server-authoritative. */
export function createSidebarChipsCollapsedStorage() {
  const storage = createJSONStorage<boolean>("mux.sidebar-chips-collapsed.v1", { decode: (value) => value === true }, localStorageSource)
  return {
    read: () => storage.read(false),
    write: (collapsed: boolean) => storage.write(collapsed, { removeWhen: (value) => !value }),
  }
}

/** UI preference: whether the sidebar's Working tray lists only Oracle chats, on this device. Not server-authoritative. */
export function createSidebarOraclesOnlyStorage() {
  const storage = createJSONStorage<boolean>("mux.sidebar-oracles-only.v1", { decode: (value) => value === true }, localStorageSource)
  return {
    read: () => storage.read(false),
    write: (on: boolean) => storage.write(on, { removeWhen: (value) => !value }),
  }
}
